/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AccountingWipApplyRequestDto,
  AccountingWipApplyResponseDto,
  AccountingWipCreateAndPrintReportDto,
  AccountingWipCreateAndPrintRequestDto,
  AccountingWipInitDto,
  AccountingWipJobDto,
  AccountingWipJournalLogItemDto,
  AccountingWipMonthDto,
  AccountingWipPrintRequestDto,
  AccountingWipRecalculateRequestDto,
  AccountingWipRecalculateResponseDto,
  AccountingWipReportRequestDto,
  AccountingWipXeroAccountDto,
  Month,
  WipEntryPoint,
} from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  IS_REGION_AU,
  NumberTypes,
  setDirty,
  setEntityField,
} from '@gh/common-shared';
import { WithDirty, WithEntityState, WithRecalculationNeeded } from '@gh/common-shared-data';
import { Enum, WithPrivilegedToken } from '@gh/core-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { omit } from 'lodash';

export type WipMonth = AccountingWipMonthDto;
export type WipXeroAccount = AccountingWipXeroAccountDto;
export type WipJournalLogItem = AccountingWipJournalLogItemDto;
export type WipJob = WithDirty<WithEntityState<AccountingWipJobDto>>;

export type WipMasterInfo = WithPrivilegedToken<{
  months: WipMonth[];
  currentMonth?: WipMonth;
  excludeDpp: boolean;
}>;

export enum WipTab { Jobs = 'JOBS', Logs = 'LOGS' }

export type WipDetails = WithRecalculationNeeded<WithPrivilegedToken<{
  masterInfo: FormStateSnapshot<WipMasterInfo>;
  jobs: WipJob[];
  selectedTab: WipTab;
  focusedCell?: FocusedCell;
  entryPoint?: WipEntryPoint;
  isCurrentFinancialYear: boolean;
  wipAssetAccount: WipXeroAccount;
  wipOpening: WipXeroAccount;
  wipClosing: WipXeroAccount;
  endOfTheYear: boolean;
}>>;

export type WipPrintRequest = AccountingWipPrintRequestDto;
export type WipCreateAndPrintRequest = AccountingWipCreateAndPrintRequestDto;
export type WipCreateAndPrintResponse = AccountingWipCreateAndPrintReportDto;
export type WipReportRequest = AccountingWipReportRequestDto;

export type WipJournalLogShortItem = {
  code: string;
  name: string;
  credit?: number;
  debit?: number;
};

export type WipApplyResponse = AccountingWipApplyResponseDto;
export type WipRecalculateResponse = AccountingWipRecalculateResponseDto;

export const getWipJobId = (job: WipJob) => job.jobId;
export const getWipJournalLogItemId = (job: WipJournalLogItem) => job.wjlId;

export const wipJobArr = createArrayHelper(getWipJobId);

export const WIP_EMPTY_MASTER_INFO: WipMasterInfo = {
  months: [],
  currentMonth: void 0,
  excludeDpp: true,
};

export const WIP_DETAILS_INIT: WipDetails = {
  masterInfo: formStateSnapshot(WIP_EMPTY_MASTER_INFO),
  jobs: [],
  selectedTab: WipTab.Jobs,
  isCurrentFinancialYear: false,
  wipAssetAccount: <any>void 0,
  wipOpening: <any>void 0,
  wipClosing: <any>void 0,
  endOfTheYear: false,
};

export const WIP_JOB_ENTITY_CONFIG: EntityConfig<WipJob> = {
  overrunsAdjust: { type: NumberTypes.SignedPrice },
};

export const validateWipJobField = fieldValidatorByEntityConfig(WIP_JOB_ENTITY_CONFIG);
export const wipJobFieldState = fieldStateByEntityConfig(WIP_JOB_ENTITY_CONFIG);

export const wipDetailsFromInitDto = (init: AccountingWipInitDto): WipDetails => ({
  masterInfo: formStateSnapshot({
    months: init.months,
    currentMonth: void 0,
    excludeDpp: true,
  }),
  jobs: [],
  selectedTab: WipTab.Jobs,
  isCurrentFinancialYear: init.isCurrentFinancialYear,
  wipAssetAccount: init.wipAssetAccount,
  wipOpening: init.wipOpening,
  wipClosing: init.wipClosing,
  endOfTheYear: init.endOfTheYear,
});

export const wipDetailsToApplyRequestDto = (details: WipDetails): AccountingWipApplyRequestDto => {
  const { currentMonth, excludeDpp } = getFormStateSnapshotValue(details.masterInfo);
  return {
    month: currentMonth!.month,
    year: currentMonth!.year,
    showDpp: !excludeDpp,
  };
};

export const wipDetailsToRecalculateRequestDto = (details: WipDetails,
                                                  isPersist: boolean): AccountingWipRecalculateRequestDto => {
  const { currentMonth, excludeDpp } = getFormStateSnapshotValue(details.masterInfo);
  const { month, year } = currentMonth!;
  return {
    month,
    year,
    isPersist,
    journal: details.jobs.map((job) => <AccountingWipJobDto>{
      ...omit(job, 'entityState', 'isDirty'),
      isExcludeDpp: excludeDpp,
    }),
  };
};

export const wipJobsToCreateAndPrintRequestDto = (details: WipCreateAndPrintRequest): AccountingWipCreateAndPrintRequestDto => ({
    journal: details.journal.map((job) => <AccountingWipJobDto>{
      ...omit(job, 'entityState', 'isDirty'),
    }),
    totals: details.totals,
  });

export const wipDetailsToCloseEmptyYearRequestDto = (details: WipDetails) => {
  const dto = wipDetailsToRecalculateRequestDto(details, true);
  return {
    ...dto,
    privilegedTokens: details.masterInfo.value.privilegedTokens,
    journal: dto.journal.map((job) => ({ ...job, overrunsAdjust: 0 })),
  };
};

export const wipDetailsToPrintRequestDto = (details: WipDetails): AccountingWipReportRequestDto => {
  const { currentMonth } = getFormStateSnapshotValue(details.masterInfo);
  return currentMonth!;
};

export const WIP_MASTER_INFO_ENTITY_CONFIG: EntityConfig<WipMasterInfo> = {
  currentMonth: { required: true },
};

export const validateWipMasterInfo = entityValidatorByEntityConfig(WIP_MASTER_INFO_ENTITY_CONFIG);
export const setWipJobField = composeSetters(
  setEntityField,
  fieldValidationSetter(validateWipJobField),
  setDirty);

export const calculateWipJobTotal = (jobs: WipJob[]): WipJob => {
  const total = <any>jobs.reduce(
    (acc, job) => ({
      jobNumber: job.jobNumber,
      customerName: job.customerName,
      totalPrice: acc.totalPrice + job.totalPrice,
      drawnToDate: acc.drawnToDate + job.drawnToDate,
      drawnToDatePct: acc.drawnToDatePct + job.drawnToDatePct,
      budgetedCost: acc.budgetedCost + job.budgetedCost,
      invoicesToDate: acc.invoicesToDate + job.invoicesToDate,
      invoicesToDatePct: acc.invoicesToDatePct + job.invoicesToDatePct,
      budgetedProfit: acc.budgetedProfit + job.budgetedProfit,
      underruns: acc.underruns + job.underruns,
      overruns: acc.overruns + job.overruns,
      overrunsAdjust: acc.overrunsAdjust + (job.overrunsAdjust || 0),
      wip: acc.wip + job.wip,
      wipAasb111: acc.wipAasb111 + (job.wipAasb111 || 0),
    }),
    {
      jobNumber: '',
      customerName: '',
      totalPrice: 0,
      drawnToDate: 0,
      drawnToDatePct: 0,
      budgetedCost: 0,
      invoicesToDate: 0,
      invoicesToDatePct: 0,
      budgetedProfit: 0,
      underruns: 0,
      overruns: 0,
      overrunsAdjust: 0,
      wip: 0,
      wipAasb111: 0,
    });

  total.jobId = '';
  total.wpaId = '';
  if (jobs.length > 0) {
    total.drawnToDatePct /= jobs.length;
    total.invoicesToDatePct /= jobs.length;
  }

  return total;
};

export const wipDetailsToJournal1 = (details: WipDetails): WipJournalLogShortItem[] => {
  const { wipAssetAccount, wipClosing } = details;
  const total = calculateWipJobTotal(details.jobs);
  const totalPrice = !IS_REGION_AU  ? total.wip : total.wipAasb111;
  return [{
    code: wipAssetAccount.code,
    name: wipAssetAccount.name,
    debit: totalPrice >= 0 ? totalPrice : void 0,
    credit: totalPrice >= 0 ? void 0 : Math.abs(totalPrice),
  }, {
    code: wipClosing.code,
    name: wipClosing.name,
    debit: totalPrice >= 0 ? void 0 : Math.abs(totalPrice),
    credit: totalPrice >= 0 ? totalPrice : void 0,
  }];
};

export const wipDetailsToJournal2 = (details: WipDetails): WipJournalLogShortItem[] => {
  const { endOfTheYear, wipAssetAccount, wipOpening, wipClosing } = details;
  const total = calculateWipJobTotal(details.jobs);
  const totalPrice = !IS_REGION_AU ? total.wip : total.wipAasb111;
  return [{
    code: !endOfTheYear ? wipClosing.code : wipOpening.code,
    name: !endOfTheYear ? wipClosing.name : wipOpening.name,
    debit: totalPrice >= 0 ? totalPrice : void 0,
    credit: totalPrice >= 0 ? void 0 : Math.abs(totalPrice),
  }, {
    code: wipAssetAccount.code,
    name: wipAssetAccount.name,
    debit: totalPrice >= 0 ? void 0 : Math.abs(totalPrice),
    credit: totalPrice >= 0 ? totalPrice : void 0,
  }];
};
export const getWipSelectedDate = (details: WipDetails) => {
  const { currentMonth } = getFormStateSnapshotValue(details.masterInfo);
  return currentMonth ? new Date(currentMonth.year, Enum.toValue(Month, currentMonth.month) - 1) : void 0;
};

export const wipCreateAndPrintReportData = (jobs: WipJob[], totals: WipJob) =>
  ({
    journal: jobs,
    totals: {
      totalPrices: totals.totalPrice,
      totalBudgetCost: totals.budgetedCost,
      totalInvoices: totals.invoicesToDate,
      totalBudgetProfit: totals.budgetedProfit,
      totalOver: totals.overruns,
      totalUnder: totals.underruns,
      totalOverAdj: totals.overrunsAdjust,
      totalWip: totals.wip,
      totalAasb: totals.wipAasb111,
      totalClaimed: totals.drawnToDate,
    },
  });
